import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { API, formatLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import EditorialFooter from "@/components/EditorialFooter";
import CiteThisEntry from "@/components/CiteThisEntry";
import { Calendar, MapPin, ArrowLeft, ExternalLink, Share2, FileText, Quote, Image as ImageIcon, Copy } from "lucide-react";
import { toast } from "sonner";

const IMPACT_BG = {
  restricts: "bg-[#D32F2F] text-white",
  protects: "bg-[#008A00] text-white",
  mixed: "bg-[#0050FF] text-white",
};

export default function EntryDetail() {
  const { id } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get(`/entries/${id}`)
      .then((r) => setEntry(r.data))
      .catch((err) => console.error("Failed to load entry", err))
      .finally(() => setLoading(false));
  }, [id]);

  const ogImageUrl = entry ? `${API}/og/entry/${entry.id}.png` : "";
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const shareUrl = entry ? `${PUBLIC_URL}/entry/${entry.id}` : "";

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: entry.title, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard");
      }
    } catch (err) {
      console.error("Share failed", err);
    }
  };

  const copyOg = async () => {
    try {
      await navigator.clipboard.writeText(ogImageUrl);
      toast.success("Share card image URL copied");
    } catch (err) {
      console.error("Copy OG URL failed", err);
    }
  };

  if (loading) return <div className="max-w-[1100px] mx-auto px-6 py-12 font-mono-plex text-sm uppercase">Loading…</div>;
  if (!entry) return <div className="max-w-[1100px] mx-auto px-6 py-12">Entry not found.</div>;

  const description = (entry.summary || "").slice(0, 300);

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <Helmet>
        <title>{`${entry.title} — Receipts`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={shareUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Receipts" />
        <meta property="og:title" content={entry.title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${entry.title} — ${formatLabel(entry.impact)} · ${entry.jurisdiction}`} />
        <meta property="og:url" content={shareUrl} />
        <meta property="article:published_time" content={entry.date_action} />
        <meta property="article:section" content={formatLabel(entry.category)} />
        {(entry.tags || []).map((t) => (
          <meta property="article:tag" content={t} key={t} />
        ))}
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={entry.title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="twitter:image:alt" content={`${entry.title} — ${formatLabel(entry.impact)} · ${entry.jurisdiction}`} />
      </Helmet>
      <Link to="/tracker" className="inline-flex items-center gap-2 text-sm font-mono-plex uppercase tracking-wider hover:underline" data-testid="back-to-tracker">
        <ArrowLeft className="w-4 h-4" /> Back to tracker
      </Link>

      <div className="mt-6 border-b border-black/15 pb-6">
        <div className="flex items-center flex-wrap gap-2">
          <span className={`font-mono-plex text-xs uppercase tracking-wider px-2.5 py-1 ${IMPACT_BG[entry.impact]}`}>
            {formatLabel(entry.impact)}
          </span>
          <span className="font-mono-plex text-xs uppercase tracking-wider px-2.5 py-1 border border-black">
            {formatLabel(entry.status)}
          </span>
          <span className="font-mono-plex text-xs uppercase tracking-wider text-[#555]">
            {formatLabel(entry.action_type)} · {formatLabel(entry.category)}
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-4 leading-[1.05]">{entry.title}</h1>
        <div className="mt-5 flex items-center gap-6 flex-wrap text-sm font-mono-plex uppercase tracking-wider text-[#555]">
          <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" />{entry.date_action}</span>
          <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" />{entry.jurisdiction}</span>
          <span className="inline-flex items-center gap-2"><FileText className="w-4 h-4" />{entry.citations?.length || 0} receipts on file</span>
          {isLegacy(entry) && (
            <span className="border border-[#555] px-2 py-0.5 text-[11px]" title="Published before the current standard of 3+ verified citations reviewed by a second editor" data-testid="legacy-entry-badge">
              Legacy entry · predates current review standard
            </span>
          )}
          <button onClick={share} className="inline-flex items-center gap-2 underline underline-offset-4 hover:text-black" data-testid="share-entry-btn">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button onClick={() => setPreviewOpen(true)} className="inline-flex items-center gap-2 underline underline-offset-4 hover:text-black" data-testid="preview-card-btn">
            <ImageIcon className="w-4 h-4" /> Preview share card
          </button>
          <CiteThisEntry entry={entry} pageUrl={shareUrl} />
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 mt-10">
        <article className="lg:col-span-7 space-y-5">
          <p className="text-xl leading-relaxed font-display">{entry.summary}</p>
          {entry.body && (
            <div className="text-base leading-relaxed text-[#222] whitespace-pre-wrap">{entry.body}</div>
          )}

          {entry.actors?.length > 0 && (
            <div className="pt-6 border-t border-black/10">
              <div className="overline">/actors</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {entry.actors.map((a) => (
                  <span key={a} className="font-mono-plex text-xs uppercase tracking-wider px-2.5 py-1 bg-[#EAEAEA]">{a}</span>
                ))}
              </div>
            </div>
          )}

          {entry.tags?.length > 0 && (
            <div className="pt-6 border-t border-black/10">
              <div className="overline">/tags</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {entry.tags.map((t) => (
                  <span key={t} className="font-mono-plex text-xs lowercase tracking-wider px-2.5 py-1 border border-black/30">#{t}</span>
                ))}
              </div>
            </div>
          )}
        </article>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 bg-white border border-black/15">
            <div className="bg-black text-white px-5 py-3 font-mono-plex uppercase tracking-[0.25em] text-xs flex items-center gap-2">
              <Quote className="w-3.5 h-3.5" /> The Receipts
            </div>
            <div className="divide-y divide-black/10">
              {(entry.citations || []).map((c, i) => (
                <div key={`${c.url || "nourl"}-${i}`} className="p-5" data-testid={`citation-${i}`}>
                  <div className="font-mono-plex text-[11px] tracking-[0.25em] uppercase text-[#555]">№ {String(i + 1).padStart(2, "0")} · {c.publisher || "Source"}</div>
                  <div className="font-display text-lg font-bold mt-1.5 leading-snug">{c.title}</div>
                  {c.date && <div className="font-mono-plex text-xs text-[#555] mt-1">{c.date}</div>}
                  {c.quote && (
                    <blockquote className="mt-3 border-l-2 border-black pl-3 italic text-sm text-[#222]">
                      "{c.quote}"
                    </blockquote>
                  )}
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`citation-link-${i}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-plex uppercase tracking-wider underline underline-offset-4 break-all"
                  >
                    Read source <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
              {(entry.citations || []).length === 0 && (
                <div className="p-5 text-sm text-[#555]">No receipts attached yet.</div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <EditorialFooter entry={entry} />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="rounded-none max-w-3xl border-2 border-black p-0 gap-0 bg-white">
          <DialogHeader className="px-6 py-4 border-b border-black/15">
            <DialogTitle className="font-display text-2xl font-bold tracking-tight" data-testid="preview-dialog-title">Share card preview</DialogTitle>
            <DialogDescription className="text-xs font-mono-plex uppercase tracking-wider text-[#555]">
              This is the 1200×630 image social platforms will render when this entry is shared.
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 bg-[#F5F5F5]">
            <img
              src={ogImageUrl}
              alt={`Open Graph preview for ${entry.title}`}
              className="w-full border border-black/30"
              data-testid="og-preview-image"
            />
          </div>
          <div className="px-6 py-4 border-t border-black/15 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between bg-white">
            <code className="font-mono-plex text-[11px] text-[#555] truncate">{ogImageUrl}</code>
            <div className="flex gap-2">
              <Button onClick={copyOg} variant="outline" className="rounded-none border-black h-9" data-testid="copy-og-url-btn">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy image URL
              </Button>
              <Button onClick={share} className="rounded-none bg-black text-white h-9" data-testid="dialog-share-btn">
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function isLegacy(entry) {
  const verified = (entry.citations || []).filter((c) => c.verified).length;
  return verified < 3 || (entry.created_by && entry.created_by === entry.reviewed_by);
}
